import type { Mission } from '@/features/missions/api/models'
import { nectarPaths } from '@/pages/routes'
import { plural, t } from '@lingui/core/macro'
import type { NectarEnrollmentStatus, NectarProject } from './models'

function mapEnrollmentStatus(status: NectarEnrollmentStatus | undefined): Mission['view']['latestSubmissionStatus'] {
  if (!status) return null
  switch (status) {
    case 'APPROVED':
      return 'COMPLETED'
    case 'REJECTED':
    case 'EXPIRED':
      return 'FAILED'
    case 'PENDING_REVIEW':
      return 'SUBMITTED'
    case 'IN_PROGRESS':
      return 'IN_PROGRESS'
    default:
      return null
  }
}

function buildTags(project: NectarProject): string[] {
  const status = project.enrollment?.status
  if (!status) return [t`Hot`]
  if (status !== 'IN_PROGRESS') return []
  if (project.type === 'VOICE_CONVERSATION') {
    const left = project.maxTasksPerUser - project.progress.completed
    return left > 0 ? [plural(left, { one: '# left', other: '# left' })] : []
  }
  if (project.type === 'PROFICIENCY_ASSESSMENT') {
    return [t`Assessment`]
  }
  const progress = `${project.progress.completed}/${project.progress.total}`
  return [t`${progress} done`]
}

export function nectarProjectToMission(project: NectarProject, basePath: string): Mission {
  const isTasksExhausted = project.enrolled && project.progress.completed >= project.maxTasksPerUser

  let submissionStatus = mapEnrollmentStatus(project.enrollment?.status)
  if (isTasksExhausted && submissionStatus === 'IN_PROGRESS') {
    submissionStatus = 'SUBMITTED'
  }

  return {
    id: `nectar-${project.id}`,
    availability: {
      available: project.availability?.available,
      reason:
        (project.availability?.reasons || []).find((reason) => reason.type === 'not_whitelisted')
          ?.type ??
        (project.availability?.reasons || []).find(
          (reason) => reason.type === 'quota_unavailable' || reason.type === 'profile_language'
        )?.type ??
        undefined
    },
    sortOrder: project.sortOrder,
    title: project.name,
    type: project.type,
    status: project.status === 'ACTIVE' ? 'ENABLED' : 'DISABLED',
    reward: project.enrollment?.earnedReward ? project.enrollment?.earnedReward : (project.totalReward ?? 0),
    rewardType: project.rewardConfig?.type === 'usd' ? 'usd' : 'points',
    shortDescription: project.description ?? '',
    tags: buildTags(project),
    description: { sections: [] },
    detailPath: nectarPaths.tasks(project.id, basePath),
    view: {
      accruedReward: project.enrollment?.earnedReward ?? 0,
      joined: project.enrolledUsersCount,
      latestSubmissionStatus: submissionStatus,
      nextSubmissionAvailable: !project.enrolled,
      nextSubmissionReward: project.totalReward ?? 0
    }
  }
}
